// Maps a role's scope to its nested REST collection/item path.
//
// Backend: apps/admin-api/src/routes/roles (matrix X). The single /api/user-roles
// endpoint was split into one nested resource per scope, so the parent id now lives
// in the URL. Scope spellings are tolerated in both forms used across the app
// ('system'|'sys_admin', 'supplier'|'ad_supplier').

interface ScopeOwner {
  scope_type?: string | null
  provider_id?: number | null
  clinic_id?: number | null
  supplier_id?: number | null
  clinic_group_id?: number | null
  ad_supplier_group_id?: number | null
}

/** The parent id that belongs to a role/owner given its scope_type. */
export function rolesParentId(owner: ScopeOwner): number | null {
  switch (owner.scope_type) {
    case 'provider':
      return owner.provider_id ?? null
    case 'clinic':
      return owner.clinic_id ?? null
    case 'ad_supplier':
    case 'supplier':
      return owner.supplier_id ?? null
    case 'clinic_group':
      return owner.clinic_group_id ?? null
    case 'ad_supplier_group':
      return owner.ad_supplier_group_id ?? null
    default:
      return null // system / sys_admin — global, no parent
  }
}

/** Build the nested roles path for a scope + parent, optionally addressing one role. */
export function buildRolesPath(
  scope: string | null | undefined,
  parentId: number | null | undefined,
  roleId?: number,
): string {
  let base: string
  switch (scope) {
    case 'provider':
      base = `/api/providers/${parentId}/roles`
      break
    case 'clinic':
      base = `/api/clinics/${parentId}/roles`
      break
    case 'ad_supplier':
    case 'supplier':
      base = `/api/ad-suppliers/${parentId}/roles`
      break
    case 'clinic_group':
      base = `/api/clinic-groups/${parentId}/roles`
      break
    case 'ad_supplier_group':
      base = `/api/ad-supplier-groups/${parentId}/roles`
      break
    case 'system':
    case 'sys_admin':
    default:
      base = `/api/system/roles`
      break
  }
  return roleId != null ? `${base}/${roleId}` : base
}

/** Derive the full path straight from a role-like object (scope_type + its parent id). */
export function rolesPathForOwner(owner: ScopeOwner, roleId?: number): string {
  return buildRolesPath(owner.scope_type, rolesParentId(owner), roleId)
}
